import { Quote } from "lucide-react"

import { FloatingElement, MotionDiv, MotionList, MotionSection, blurIn, fadeInLeft, scaleIn } from "@/components/motion"
import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"

export type ProcessStep = {
  title: string
  description: string
}

export type ProofQuote = {
  quote: string
  name: string
  role?: string
}

export type ProofAndProcessProps = {
  eyebrow?: string
  title: string
  description?: string
  steps: ProcessStep[]
  proof?: ProofQuote
  className?: string
}

/** Editorial process section with built-in proof moment and asymmetric rhythm. */
export function ProofAndProcess({ eyebrow, title, description, steps, proof, className }: ProofAndProcessProps) {
  return (
    <MotionSection className={cn("relative overflow-hidden bg-muted/48 py-24 sm:py-32", className)}>
      <FloatingElement intensity={12} duration={7} className="absolute right-0 top-0 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
      <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.88fr_1.12fr] lg:px-8">
        <MotionDiv variants={fadeInLeft} className="lg:sticky lg:top-28 lg:self-start">
          {eyebrow && <Badge variant="secondary" className="mb-5 rounded-full px-3 py-1">{eyebrow}</Badge>}
          <h2 className="font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
            {title}
          </h2>
          {description && <p className="mt-5 text-lg leading-8 text-muted-foreground">{description}</p>}

          {proof && (
            <MotionDiv variants={scaleIn}>
              <Card className="mt-10 overflow-hidden rounded-panel border-border/70 bg-background p-7 shadow-[0_20px_70px_hsl(var(--foreground)/0.1)]">
              <Quote className="mb-5 size-8 text-primary" />
              <blockquote className="text-xl font-medium leading-8 tracking-tight text-foreground">
                &ldquo;{proof.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-6 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{proof.name}</span>
                {proof.role && <span> / {proof.role}</span>}
              </figcaption>
              </Card>
            </MotionDiv>
          )}
        </MotionDiv>

        <MotionList className="space-y-4">
          {steps.map((step, index) => (
            <MotionDiv key={step.title} variants={blurIn}>
              <Card className="group overflow-hidden rounded-panel border-border/70 bg-background p-6 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_60px_hsl(var(--foreground)/0.1)] sm:p-8">
                <div className="flex gap-5">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-card bg-primary text-sm font-bold text-primary-foreground shadow-[var(--shadow-elegant)]">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-semibold tracking-tight text-foreground">{step.title}</h3>
                    <p className="mt-3 text-base leading-7 text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              </Card>
            </MotionDiv>
          ))}
        </MotionList>
      </div>
    </MotionSection>
  )
}
